/* AF Fits catalogue.
   Loads the store's /products.json and applies the review's data fixes in the browser:
   colours merged into one style, names in title case, sizes in order, a product type,
   and per-unit prices on packs. Real variant SKUs win; generated ones fill the gaps.
   The result is cached for the visit so every page after the first renders at once. */
(() => {
  const SIZE_ORDER = ['XXS', '2XS', 'XS', 'S', 'M', 'L', 'XL', '2XL', 'XXL', '3XL', 'XXXL', '4XL', '5XL'];
  const KEEP_UPPER = new Set(['AF', 'RM', 'SM', 'XL', '2XL', '3XL', '4XL', 'XS']);
  const SWATCH = [
    [/black/, '#141414'], [/white|cream/, '#f2f0ec'], [/light ?gr[ae]y|ash|sport ?gr[ae]y/, '#c4c4c4'],
    [/dark ?gr[ae]y|charcoal|shadow/, '#4b4b4b'], [/gr[ae]y/, '#8d8d8d'], [/purple/, '#6b2fd0'], [/brown/, '#6e4b33'],
    [/tan|khaki|beige/, '#c6ad86'], [/navy|blue/, '#2c4d9c'], [/pink/, '#e58fb5'], [/lime|green/, '#8fd14f'],
  ];
  const ROOT = ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/').replace(/\/?$/, '/');
  const CACHE_KEY = 'affits-catalog-v1';
  const CACHE_MS = 10 * 60 * 1000;

  const SMALL = new Set(['of', 'and', 'with', 'for', 'the', 'a', 'x']);
  const titleCase = (s) => s.replace(/\S+/g, (w, i) => {
    const bare = w.replace(/[^A-Za-z0-9]/g, '');
    if (KEEP_UPPER.has(bare.toUpperCase()) && bare.length <= 3) return w.toUpperCase();
    if (i > 0 && SMALL.has(w.toLowerCase())) return w.toLowerCase();
    return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
  }).replace(/\bAf\b/g, 'AF');

  const split = (title) => {
    const m = title.match(/^(.*?)\s*\(([^)]*)\)\s*(.*)$/);
    if (m) return { base: (m[1] + (m[3] ? ` ${m[3]}` : '')).replace(/\s+-\s*$/, '').trim(), colour: m[2].trim() };
    const tail = title.match(/^(.*?)\s+(White|Black|Beige|Dark Grey|Grey)$/i);
    if (tail) return { base: tail[1].trim(), colour: tail[2] };
    return { base: title.trim(), colour: '' };
  };

  const kindOf = (title, tags) => {
    const t = `${title} ${tags}`.toLowerCase();
    if (/packs? of|box of|bulk/.test(t)) return 'Bulk packs';
    if (/rack|lanyard|name tag|fob/.test(t)) return 'Club supplies';
    if (/bottle|shaker|jug/.test(t)) return 'Bottles';
    if (/\bbag\b|tote/.test(t)) return 'Bags';
    if (/sock/.test(t)) return 'Socks';
    if (/hat|beanie|toque|headwear/.test(t)) return 'Headwear';
    if (/raincoat|jacket/.test(t)) return 'Jackets';
    if (/sweatpant|pants/.test(t)) return 'Pants';
    if (/\bbra\b/.test(t)) return 'Bras';
    if (/legging/.test(t)) return 'Leggings';
    if (/short/.test(t)) return 'Shorts';
    if (/active wear set/.test(t)) return 'Sets';
    if (/hoodie/.test(t)) return 'Hoodies';
    if (/sweater/.test(t)) return 'Sweaters';
    if (/polo|long sleeve/.test(t)) return 'Long sleeves and polos';
    if (/tank/.test(t)) return 'Tanks';
    if (/band/.test(t)) return 'Accessories';
    return 'T-shirts';
  };

  const collectionsOf = (tags, title) => {
    const t = `${tags} ${title}`.toLowerCase(); const out = [];
    if (t.includes('summer 2026')) out.push('Summer 2026');
    if (t.includes('premium')) out.push('Premium');
    if (t.includes('performance')) out.push('Performance');
    if (/sab[eē]/.test(t)) out.push('Sabē Athletic');
    if (t.includes('limited') || /sab[eē]/.test(t)) out.push('Limited edition');
    if (/\bwomen|\bbra\b|legging|sab[eē]/.test(t)) out.push("Women's");
    if (t.includes('uniform') || t.includes('polo')) out.push('Uniforms');
    return out;
  };

  // "purple/white" is a purple garment with a white logo: the first name is the garment colour.
  const swatchOf = (colour) => {
    const c = colour.toLowerCase().split(/[\/,]/)[0];
    for (const [re, hex] of SWATCH) if (re.test(c)) return hex;
    return '#9a8fb0';
  };

  const packSize = (title) => { const m = title.match(/(?:packs? of|box of)\s*(\d+)/i); return m ? Number(m[1]) : 0; };

  /* ---------- SKUs ----------
     Variants that already carry a SKU in Shopify keep it. The rest get one from the same scheme:
       AFF-HOD012-BR-M  =  brand - type code + style number - colour code - size
     The part without the size (AFF-HOD012-BR) is the style number for one colour. */
  const TYPE_CODE = {
    'T-shirts': 'TEE', Hoodies: 'HOD', Sweaters: 'SWT', Pants: 'PNT', Shorts: 'SHO', Jackets: 'JKT', Headwear: 'HAT',
    Bags: 'BAG', Socks: 'SOK', Bottles: 'BTL', Sets: 'SET', Tanks: 'TNK', 'Long sleeves and polos': 'LSP',
    'Bulk packs': 'PCK', 'Club supplies': 'CLB', Accessories: 'ACC', Bras: 'BRA', Leggings: 'LEG',
  };
  const COLOUR_CODE = [
    [/sport ?gr[ae]y/, 'SG'], [/light ?gr[ae]y/, 'LG'], [/dark ?gr[ae]y/, 'DG'], [/charcoal/, 'CH'], [/shadow/, 'SH'], [/ash/, 'AS'],
    [/gr[ae]y/, 'GY'], [/black/, 'BK'], [/white/, 'WH'], [/purple/, 'PU'], [/brown/, 'BR'], [/tan/, 'TN'], [/khaki/, 'KH'],
    [/beige/, 'BE'], [/cream/, 'CR'], [/navy/, 'NV'], [/blue/, 'BL'], [/pink/, 'PK'], [/lime/, 'LM'], [/green/, 'GN'],
  ];
  const colourCode = (colour) => {
    const parts = colour.toLowerCase().replace(/\baf\b/g, '').split(/[\/,]+/).map((p) => p.trim()).filter(Boolean);
    if (!parts.length) return 'OS';
    return parts.map((p) => {
      const hit = COLOUR_CODE.find(([re]) => re.test(p));
      return hit ? hit[1] : p.replace(/[^a-z]/g, '').slice(0, 2).toUpperCase();
    }).join('');
  };
  const sizeCode = (v) => {
    const s = (v || '').toUpperCase().trim();
    if (!s || s === 'DEFAULT TITLE') return 'OS';
    if (s === 'XXL') return '2XL';
    if (/SINGLE/.test(s)) return '1';
    const box = s.match(/(\d+)/);
    if (/BOX|PACK/.test(s) && box) return box[1];
    return s.replace(/[^A-Z0-9]/g, '').slice(0, 6);
  };
  // Image URLs are kept whole (minus the version query) so a width can be added later.
  const imgUrl = (src) => (src ? src.split('?')[0] : '');

  // Turn raw Shopify products into styles: one entry per base name, colours inside.
  const toStyles = (products) => {
    const map = new Map();
    products.forEach((p, idx) => {
      const { base, colour } = split(p.title);
      const key = base.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
      const tags = Array.isArray(p.tags) ? p.tags : String(p.tags || '').split(',').map((t) => t.trim()).filter(Boolean);
      const sizeOpt = p.options.find((o) => /size/i.test(o.name));
      const colourOpt = p.options.find((o) => /colou?r/i.test(o.name));
      const prices = p.variants.map((v) => Number(v.price));
      const entry = map.get(key) || {
        key, name: titleCase(base), kind: kindOf(p.title, tags.join(',')), collections: new Set(),
        colours: [], sizes: new Set(), min: Infinity, max: 0, order: idx, pack: packSize(p.title), tags: new Set(), added: 0,
      };
      collectionsOf(tags.join(','), p.title).forEach((c) => entry.collections.add(c));
      tags.forEach((t) => entry.tags.add(t.toLowerCase()));
      if (sizeOpt) sizeOpt.values.forEach((s) => entry.sizes.add(s.toUpperCase() === 'XXL' ? '2XL' : s.toUpperCase()));
      entry.min = Math.min(entry.min, ...prices); entry.max = Math.max(entry.max, ...prices);
      // Newest colour's publish date, for the New link and the Newest sort.
      entry.added = Math.max(entry.added, Date.parse(p.published_at || p.created_at || '') || 0);
      const images = (p.images || []).map((im) => imgUrl(im.src)).filter(Boolean);
      entry.colours.push({
        name: colour ? titleCase(colour.replace(/\s*af$/i, '')) : '', rawColour: colour, swatch: swatchOf(colour || 'other'), handle: p.handle,
        img: images[0] || '', img2: images[1] || '', images,
        available: p.variants.some((v) => v.available),
        body: p.body_html || '',
        sizeIndex: sizeOpt ? p.options.indexOf(sizeOpt) : -1,
        variants: p.variants.map((v) => ({
          id: v.id, price: Number(v.price), available: v.available !== false, title: v.title, realSku: (v.sku || '').trim(),
          colour: colourOpt ? v[`option${p.options.indexOf(colourOpt) + 1}`] : '',
          size: sizeOpt ? v[`option${p.options.indexOf(sizeOpt) + 1}`] : (colourOpt || v.title === 'Default Title' ? '' : v.title),
        })),
      });
      map.set(key, entry);
    });
    // Style numbers run per type, in name order, so they stay stable as products are added.
    const list = [...map.values()].sort((a, b) => a.kind.localeCompare(b.kind) || a.key.localeCompare(b.key));
    const perType = {}; const used = new Set();
    list.forEach((s) => {
      const code = TYPE_CODE[s.kind] || 'GEN';
      perType[code] = (perType[code] || 0) + 1;
      s.styleNo = `AFF-${code}${String(perType[code]).padStart(3, '0')}`;
      s.colours.forEach((c) => {
        let cc = colourCode(c.rawColour);
        while (used.has(`${s.styleNo}-${cc}`)) cc = `${cc.replace(/\d+$/, '')}${(Number((cc.match(/\d+$/) || [1])[0]) || 1) + 1}`;
        used.add(`${s.styleNo}-${cc}`);
        c.style = `${s.styleNo}-${cc}`;
        // Colour stored as a variant option (water bottles, some merch) goes in the colour slot.
        c.variants.forEach((v) => {
          const made = v.colour ? `${s.styleNo}-${colourCode(v.colour)}-${sizeCode(v.size)}` : `${c.style}-${sizeCode(v.size)}`;
          v.sku = v.realSku || made;
        });
        // Once real SKUs are loaded, the style number is the first one without its size.
        const real = c.variants.find((v) => /^AFF-/.test(v.realSku));
        if (real) c.style = real.realSku.replace(/-[^-]+$/, '');
      });
    });
    return list.sort((a, b) => a.order - b.order).map((s) => ({
      ...s,
      collections: [...s.collections],
      tags: [...s.tags],
      sizes: [...s.sizes].sort((a, b) => SIZE_ORDER.indexOf(a) - SIZE_ORDER.indexOf(b)),
      unit: s.pack ? s.min / s.pack : 0,
      text: `${s.name} ${s.kind} ${[...s.collections].join(' ')} ${s.colours.map((c) => `${c.name} ${c.style} ${c.variants.map((v) => v.sku).join(' ')}`).join(' ')} ${[...s.tags].join(' ')}`.toLowerCase(),
    }));
  };

  // Find the style and colour for a product handle.
  const find = (styles, handle) => {
    for (const s of styles) {
      const c = s.colours.find((x) => x.handle === handle);
      if (c) return { style: s, colour: c };
    }
    return null;
  };

  // products.json returns at most 250 per page.
  const fetchAll = async (base) => {
    const all = [];
    for (let page = 1; page <= 8; page += 1) {
      const res = await fetch(`${base}?limit=250&page=${page}`, { headers: { Accept: 'application/json' } });
      if (!res.ok) throw new Error(`Catalogue request failed (${res.status})`);
      const { products } = await res.json();
      all.push(...products);
      if (products.length < 250) break;
    }
    return all;
  };

  let cache;
  const load = () => {
    if (cache) return cache;
    try {
      const saved = JSON.parse(sessionStorage.getItem(CACHE_KEY));
      if (saved && Date.now() - saved.at < CACHE_MS && !window.Shopify?.designMode) {
        cache = Promise.resolve(saved.styles);
        return cache;
      }
    } catch (_) { /* storage off */ }
    cache = fetchAll(`${ROOT}products.json`).then((products) => {
      const styles = toStyles(products);
      try { sessionStorage.setItem(CACHE_KEY, JSON.stringify({ at: Date.now(), styles })); } catch (_) { /* full or off */ }
      return styles;
    });
    cache.catch(() => { cache = null; });
    return cache;
  };

  // Handles in one Shopify collection, for collection pages.
  const collectionHandles = (handle) => fetchAll(`${ROOT}collections/${encodeURIComponent(handle)}/products.json`)
    .then((products) => new Set(products.map((p) => p.handle)));

  // Every word must match somewhere; plurals and "tee"/"tees" count as the same word.
  const search = (styles, q) => {
    const words = q.toLowerCase().split(/\s+/).filter(Boolean).map((w) => w.replace(/(ies|es|s)$/, ''));
    if (!words.length) return [];
    return styles.filter((s) => words.every((w) => s.text.includes(w)));
  };

  const money = (n) => `$${n.toFixed(2)}`;
  const img = (url, w) => (url ? `${url}${url.includes('?') ? '&' : '?'}width=${w}` : '');
  const productUrl = (handle) => `${ROOT}products/${handle}`;
  window.PNCatalog = { load, search, money, find, img, productUrl, collectionHandles, SIZE_ORDER, ROOT };
})();
